/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./EmailResetPassword.css";
import { simpleFetch } from "../../restWrapper";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import errorMsg from "../../assets/json/errMsg.json";
import { renderOrgLanguageSelector } from "../../assets/component/renderOrgLanguageSelector.js";
import { PLATFORM } from "../../config";
import PropTypes from "prop-types";
import Promise from "bluebird";

class EmailResetPassword extends Component {
  constructor() {
    super();
    this.state = {
      language: "zh",
      fetching: true,
      pw: "",
      rePw: "",
      errMsg: {
        pw: "",
        rePw: "",
      },
      completed: false,
    };
    this.btnClick = this.btnClick.bind(this);
  }
  componentDidMount() {
    const path = this.props.location.pathname;
    const token = path.split("/EmailResetPassword/")[1];
    if (!token) {
      this.fail();
    }
    this.handleToken(token);
    this.setState({
      token: token,
    });
  }

  handleUserToken(token, userType) {
    const url = "/" + userType + "/forgot/" + token;
    return simpleFetch(url, "GET").then((res) => {
      if (res.status !== 204) return false;
      return true;
    });
  }

  handleToken(token) {
    Promise.all([
      this.handleUserToken(token, "donors"),
      this.handleUserToken(token, "organizations"),
    ]).then((res) => {
      this.setState({
        fetching: false,
      });
      if (res[0] === true) {
        this.setState({
          isDonor: true,
        });
      } else if (res[1] === true) {
        this.setState({
          isOrg: true,
        });
      } else {
        this.fail();
      }
    });
  }

  fail() {
    this.props.history.push({ pathname: "/ErrorPrompt" });
  }

  /**
   * Where a finished reset goes to sign in.
   *
   * On Plug this page is reached from a Plug-branded mail on the Plug
   * domain, so /OrgLogin -- the CheckPlus org sign-in -- is neither where
   * the person came from nor somewhere they can get back from. The
   * marketplace opens its own sign-in off the query string. Same pairing
   * EmailVerified already makes (CP-304).
   */
  goSignIn() {
    if (PLATFORM === "plug") {
      this.props.history.push({ pathname: "/webapp", search: "?login=1" });
      return;
    }
    this.props.history.push({ pathname: "/OrgLogin" });
  }

  btnClick() {
    if (this.state.fetching) return false;
    let err = this.state.errMsg;
    let bool = false;
    if (!this.state.pw || this.state.pw.length < 8) {
      err.pw = errorMsg[this.state.language].pwErr.newPasswdErr;
      bool = true;
    } else {
      err.pw = "";
    }

    if (this.state.pw !== this.state.rePw) {
      err.rePw = errorMsg[this.state.language].pwErr.reEnterPwErr;
      bool = true;
    } else {
      err.rePw = "";
    }

    this.setState({
      errMsg: err,
    });

    if (bool) return false;

    this.setState({
      fetching: true,
    });
    const userType = this.state.isDonor ? "donors" : "organizations";
    const url = "/" + userType + "/reset/" + this.state.token;
    const data = {
      password: this.state.pw,
    };

    simpleFetch(url, "POST", data).then((res) => {
      if (res.status === 204) {
        if (userType === "organizations") this.goSignIn();
        else {
          this.setState({
            fetching: false,
            completed: true,
          });
        }
      } else {
        // fetching has to come back down with the error: render returns the
        // full-page loader while it is true, so without this an expired or
        // already-used token -- the ordinary end of a reset link that has
        // been sat on -- spins forever and never shows the message set here.
        err.rePw = errorMsg[this.state.language].fail;
        this.setState({
          fetching: false,
          errMsg: err,
        });
      }
    });
  }

  render() {
    if (this.state.fetching) {
      return <this.props.RenderFullPageLoading />;
    }
    const languageObj = this.props.languageObj;
    // The mail that leads here is branded per account (checkplus-backend
    // resolves it off sourcePlatform), and the build is per host, so the
    // page a Plug link opens is always the Plug build. One modifier
    // carries the mark and the brand colour rather than three conditions.
    const plugClass = PLATFORM === "plug" ? " EmailResetPassword-plug" : "";

    if (this.state.completed) {
      //TODO donor changed the pw
      /*
        Plug's done state is the marketplace's own: a green ring around a
        drawn check rather than the CheckPlus blue disc, the confirmation as
        a heading, and the way onward as a full-width gradient button. The
        tick is inline SVG because the shared .-confirmTick is a background
        image in CheckPlus blue, and the design's is a stroke in a different
        colour on a tinted circle -- two different drawings, not one drawing
        recoloured.
      */
      if (PLATFORM === "plug") {
        return (
          <div className="EmailResetPassword-confirmView EmailResetPassword-plug">
            <div className="EmailResetPassword-confirmPrompt">
              <div className="EmailResetPassword-plugDoneIcon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12.5l4.5 4.5L19 7"
                    stroke="#1f9d55"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="EmailResetPassword-plugDoneTitle">
                {languageObj.doneTitle}
              </div>
              <div className="EmailResetPassword-plugDoneText">
                {languageObj.doneText}
              </div>
              <div
                className="EmailResetPassword-plugCta"
                onClick={() => this.goSignIn()}
              >
                {languageObj.signInBtn}
              </div>
            </div>
          </div>
        );
      }
      /* The CheckPlus flow ends here, on a page with nowhere to go: a donor
         signs in on their nonprofit's own subdomain, which this page has no
         way to name. Plug has one front door for every account type, so its
         own done state above says where. The mark carries over from the form
         step so the two screens read as one flow. */
      return (
        <div className="EmailResetPassword-confirmView">
          <div className="EmailResetPassword-confirmPrompt">
            <div className="EmailResetPassword-cpHeader">
              <div className="EmailResetPassword-cpLogo" />
            </div>
            <div className="EmailResetPassword-confirmTick"></div>
            <div className="EmailResetPassword-confirmText">
              {languageObj.confirmText}
            </div>
          </div>
        </div>
      );
    }

    /*
      Plug's form is the marketplace's, which is a different shape from the
      CheckPlus one rather than a recolour of it: the field label sits above
      its input instead of inside it as placeholder text, the requirement is
      stated up front instead of only on failure, and the action is a
      full-width gradient button below both fields rather than a round arrow
      sharing a row with the second one. Branching the markup keeps CheckPlus
      byte-identical -- the alternative was reordering a shared flex row from
      a modifier class, which would have left both brands one edit away from
      breaking the other.

      The copy is the keys this page already ships in all three languages.
      The design's own strings ("Set a new password", "Choose a new password
      for you@example.com") are English-only in the canvas, and the second
      names an address this page never learns: the token is all it is given,
      and it resolves that against donors and organisations without ever
      reading back an account.
    */
    if (PLATFORM === "plug") {
      return (
        <div className="EmailResetPassword-view EmailResetPassword-plug">
          <div className="EmailResetPassword-prompt">
            {/* Mark and language on one row -- the mark left, where the
                canvas's mobile artboard puts it, and the selector opposite
                it rather than stacked under a centred logo. */}
            <div className="EmailResetPassword-plugHeader">
              <div className="EmailResetPassword-plugLogo" />
              {/* The homepage control's shape -- masked globe, then a plain
                  select with no box around it (.PlugLanding-lang, which the
                  marketplace sign-in pane already re-uses). The selector
                  itself stays renderOrgLanguageSelector so the i18n wiring is
                  the one every other page uses. */}
              <div className="EmailResetPassword-plugLang">
                <span className="EmailResetPassword-plugLangGlobe" />
                <div className="EmailResetPassword-languageSelector">
                  {renderOrgLanguageSelector()}
                </div>
              </div>
            </div>

            <div className="EmailResetPassword-plugForm">
              <div className="EmailResetPassword-plugTitle">
                {languageObj.loginText}
              </div>
              <div className="EmailResetPassword-plugSub">
                {languageObj.text}
              </div>

              <div className="EmailResetPassword-plugField">
                <label
                  className="EmailResetPassword-plugLabel"
                  htmlFor="EmailResetPassword-pw"
                >
                  {languageObj.inputFieldLong}
                </label>
                <input
                  id="EmailResetPassword-pw"
                  type="password"
                  className="EmailResetPassword-plugInput"
                  placeholder="••••••••"
                  onChange={(e) => {
                    this.setState({
                      pw: e.target.value,
                    });
                  }}
                />
              </div>
              {renderErrorMsg(this.state.errMsg.pw)}

              <div className="EmailResetPassword-plugField">
                <label
                  className="EmailResetPassword-plugLabel"
                  htmlFor="EmailResetPassword-rePw"
                >
                  {languageObj.inputField}
                </label>
                <input
                  id="EmailResetPassword-rePw"
                  type="password"
                  className="EmailResetPassword-plugInput"
                  placeholder="••••••••"
                  onChange={(e) => {
                    this.setState({
                      rePw: e.target.value,
                    });
                  }}
                />
              </div>
              {renderErrorMsg(this.state.errMsg.rePw)}

              <div className="EmailResetPassword-plugHint">
                {languageObj.pwHint}
              </div>

              <div
                className="EmailResetPassword-plugCta"
                onClick={this.btnClick}
              >
                {languageObj.submit}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={"EmailResetPassword-view" + plugClass}>
        <div className="EmailResetPassword-prompt">
          {/* The mark and the language control share the top row, the mark
              left. The page had carried no mark at all: .EmailResetPassword-
              logo was written for the 150px white page header above the card,
              and neither that header nor the logo has been rendered for a
              long time. This one belongs to the card, so it is sized and
              placed for the card rather than reviving that rule. */}
          <div className="EmailResetPassword-cpHeader">
            <div className="EmailResetPassword-cpLogo" />
            <div className="EmailResetPassword-languageBar">
              <div className="EmailResetPassword-languageSelector">
                {renderOrgLanguageSelector()}
              </div>
            </div>
          </div>

          <div className="EmailResetPassword-loginContainer">
            <div className="EmailResetPassword-loginText">
              {languageObj.loginText}
            </div>

            <div className="EmailResetPassword-text">{languageObj.text}</div>

            <div className="EmailResetPassword-inputFieldContainer">
              <input
                type="password"
                className="EmailResetPassword-inputFieldLong"
                placeholder={languageObj.inputFieldLong}
                onChange={(e) => {
                  this.setState({
                    pw: e.target.value,
                  });
                }}
              />
            </div>
            {renderErrorMsg(this.state.errMsg.pw)}

            <div className="EmailResetPassword-inputFieldContainer">
              <div className="EmailResetPassword-inputContainerLeft">
                <input
                  type="password"
                  className="EmailResetPassword-inputField"
                  placeholder={languageObj.inputField}
                  onChange={(e) => {
                    this.setState({
                      rePw: e.target.value,
                    });
                  }}
                />
              </div>
              <div className="EmailResetPassword-inputContainerRight">
                <div
                  className="EmailResetPassword-nextBtn"
                  onClick={this.btnClick}
                >
                  <div className="EmailResetPassword-nextText">
                    {languageObj.submit}
                  </div>
                </div>
              </div>
            </div>
            {renderErrorMsg(this.state.errMsg.rePw)}
            <div className="EmailResetPassword-chin" />
          </div>
        </div>
      </div>
    );
  }
}
EmailResetPassword.propTypes = {
  languageObj: PropTypes.object,
};

export default EmailResetPassword;
